// SEO utilities — titles, meta, Open Graph, Twitter Cards, canonical URLs.
//
// Brand values are READ AT CALL TIME from lib/brand.js, which SiteSettingsProvider
// populates from General Settings. They used to be module constants here, which
// meant the business name was fixed at build time and no settings change could
// reach it — the reason earlier rebrand attempts kept leaving the old name in
// page titles and share previews.
//
// Never hoist these into constants at module scope: settings load
// asynchronously, so a constant captures the placeholder, not the real name.
import { subscribeBrand, brandSupportEmail, brandPhone } from './brand';

// `let`, not `const`: these are refreshed when settings arrive. ES modules give
// importers a LIVE binding, so anything consuming the exports at the bottom of
// this file sees the updated value without changing a single call site.
let SITE_NAME = '';
let SITE_URL = '';

// The page title is built from two values that arrive at different times: the
// page's own title (on mount) and the business name (when settings load, a
// moment later). Remembering the page title lets the brand be folded in once
// it arrives, instead of the first render winning with "Page | ".
let pageTitle = null;

const applyTitle = () => {
  if (!pageTitle) return;
  document.title =
    SITE_NAME && !pageTitle.includes(SITE_NAME) ? `${pageTitle} | ${SITE_NAME}` : pageTitle;
};

/** True once a page has set its own title, so nothing else overwrites it. */
export const hasPageTitle = () => pageTitle !== null;

subscribeBrand((brand) => {
  SITE_NAME = brand.siteName;
  SITE_URL = brand.siteUrl;
  applyTitle();
});

const DEFAULT_IMAGE = '/images/home/hero-data-center.jpg';
// Social handles are not in General Settings yet. Left empty deliberately —
// emitting another business's account in Twitter Card metadata is worse than
// omitting the tag, which setOrCreateMeta already skips on empty content.
const TWITTER_HANDLE = '';

// Helper to set or create meta tags
const setOrCreateMeta = (selector, attributes, content) => {
  // Empty content REMOVES the tag. Returning early instead leaves whatever was
  // in index.html in place, which is how a stale @123bots twitter:site survived
  // every rebrand: nothing ever cleared it, it was only skipped.
  if (!content) {
    const stale = document.head.querySelector(selector);
    if (stale) stale.remove();
    return;
  }
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('meta');
    Object.entries(attributes).forEach(([key, value]) => {
      element.setAttribute(key, value);
    });
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
};

// Helper to set or create link tags
const setOrCreateLink = (rel, href, attributes = {}) => {
  if (!href) return;
  let element = document.head.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    Object.entries(attributes).forEach(([key, value]) => {
      element.setAttribute(key, value);
    });
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
};

// Main SEO function
export const setSeoMetadata = ({
  title,
  description,
  keywords,
  canonicalPath,
  ogType = 'website',
  ogImage,
  twitterCard = 'summary_large_image',
  jsonLd,
  noIndex = false,
  robots,
  author,
}) => {
  // Title — stored, then applied. Re-applied automatically if the business
  // name arrives after this call.
  if (title) {
    pageTitle = title;
    applyTitle();
  }

  // Basic meta tags
  setOrCreateMeta('meta[name="description"]', { name: 'description' }, description);
  setOrCreateMeta('meta[name="keywords"]', { name: 'keywords' }, keywords);
  setOrCreateMeta('meta[name="author"]', { name: 'author' }, author || SITE_NAME);
  
  // Robots - pass an explicit `robots` string (e.g. from a per-page SEO dropdown) to override the noIndex shorthand
  setOrCreateMeta(
    'meta[name="robots"]', 
    { name: 'robots' }, 
    robots || (noIndex ? 'noindex, nofollow' : 'index, follow')
  );

  // Open Graph
  setOrCreateMeta('meta[property="og:title"]', { property: 'og:title' }, title);
  setOrCreateMeta('meta[property="og:description"]', { property: 'og:description' }, description);
  setOrCreateMeta('meta[property="og:type"]', { property: 'og:type' }, ogType);
  setOrCreateMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, SITE_NAME);
  setOrCreateMeta('meta[property="og:image"]', { property: 'og:image' }, ogImage || DEFAULT_IMAGE);
  setOrCreateMeta('meta[property="og:locale"]', { property: 'og:locale' }, 'en_US');
  
  if (canonicalPath) {
    const fullUrl = `${SITE_URL}${canonicalPath}`;
    setOrCreateMeta('meta[property="og:url"]', { property: 'og:url' }, fullUrl);
  }

  // Twitter Card
  setOrCreateMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, twitterCard);
  setOrCreateMeta('meta[name="twitter:site"]', { name: 'twitter:site' }, TWITTER_HANDLE);
  setOrCreateMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, title);
  setOrCreateMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, description);
  setOrCreateMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, ogImage || DEFAULT_IMAGE);

  // Canonical URL
  if (canonicalPath) {
    setOrCreateLink('canonical', `${SITE_URL}${canonicalPath}`);
  }

  // JSON-LD Structured Data
  if (jsonLd) {
    // Remove existing JSON-LD
    const existingJsonLd = document.getElementById('page-json-ld');
    if (existingJsonLd) {
      existingJsonLd.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'page-json-ld';
    script.text = JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd]);
    document.head.appendChild(script);
  }
};

// Pre-built JSON-LD generators
/**
 * Organization JSON-LD.
 *
 * Everything here comes from General Settings. Contact details and social
 * profiles are OMITTED when unset rather than defaulted, because this is
 * machine-readable data that search engines treat as authoritative: a stale
 * value does not merely look wrong, it publishes another company's phone
 * number, support address and social accounts as this business's own.
 *
 * Phone, postal address and social profiles are not yet fields in General
 * Settings. Add them there and surface them here — do not hardcode them.
 */
export const generateOrganizationSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
  };

  if (SITE_URL) {
    schema.url = SITE_URL;
    schema.logo = `${SITE_URL}${DEFAULT_IMAGE}`;
  }

  const email = brandSupportEmail();
  if (email) {
    schema.contactPoint = {
      '@type': 'ContactPoint',
      contactType: 'sales',
      email,
    };
  }

  return schema;
};

export const generateLocalBusinessSchema = (location = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}${DEFAULT_IMAGE}`,
  image: `${SITE_URL}${DEFAULT_IMAGE}`,
  description: 'Custom software, web platforms and automation for business.',
  // Read at call time from Business Info. Omitted entirely when unset —
  // publishing another business's number as structured data is worse than
  // publishing none.
  telephone: brandPhone() || undefined,
  address: {
    '@type': 'PostalAddress',
    streetAddress: location.address || '',
    addressLocality: location.city || '',
    addressRegion: location.state || '',
    postalCode: location.zip || '',
    addressCountry: 'US',
  },
  geo: location.lat && location.lng ? {
    '@type': 'GeoCoordinates',
    latitude: location.lat,
    longitude: location.lng,
  } : undefined,
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '18:00',
    },
  ],
  priceRange: '$$$',
});

export const generateProductSchema = (product) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: product.name,
  description: product.description,
  image: product.images?.[0] || DEFAULT_IMAGE,
  sku: product.sku || product.id,
  brand: {
    '@type': 'Brand',
    name: product.brand || 'PUDU',
  },
  offers: {
    '@type': 'Offer',
    url: `${SITE_URL}/products/${product.slug || product.id}`,
    priceCurrency: 'USD',
    price: product.price,
    availability: product.in_stock !== false 
      ? 'https://schema.org/InStock' 
      : 'https://schema.org/OutOfStock',
    seller: {
      '@type': 'Organization',
      name: SITE_NAME,
    },
  },
});

export const generateArticleSchema = (article) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.title,
  description: article.summary || article.description,
  image: article.image || DEFAULT_IMAGE,
  datePublished: article.published_date,
  dateModified: article.updated_date || article.published_date,
  author: {
    '@type': 'Organization',
    name: SITE_NAME,
  },
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}${DEFAULT_IMAGE}`,
    },
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${SITE_URL}/research/${article.slug}`,
  },
});

export const generateBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url ? `${SITE_URL}${item.url}` : undefined,
  })),
});

export const generateFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

export const generateWebsiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/shop?search={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});

// SEO presets for common pages.
//
// Business name is NOT written into these strings: setSeoMetadata appends the
// configured site name to every title, and baking it into descriptions is how
// the previous owner's brand survived the last rebrand.
export const SEO_PRESETS = {
  home: {
    title: 'Custom Software & Web Platforms',
    description: 'Custom software, web platforms and automation for businesses that have outgrown off-the-shelf tools. Scope your project in minutes.',
    keywords: 'custom software development, web application development, business automation, full stack development, CRM development, software consultancy',
    canonicalPath: '/',
    ogType: 'website',
  },
  portfolio: {
    title: 'Our Work',
    description: 'Applications and platforms we have built and shipped — custom systems running in production for real businesses.',
    keywords: 'software portfolio, case studies, web application examples, custom software projects',
    canonicalPath: '/apps',
    ogType: 'website',
  },
  tools: {
    title: 'Free Tools',
    description: 'Free browser-based tools — no account, no upload, no catch.',
    keywords: 'free online tools, browser tools, background remover, free utilities',
    canonicalPath: '/tools',
    ogType: 'website',
  },
  demo: {
    title: 'Schedule a Consultation',
    description: 'Book a free consultation to scope your project — timeline, budget range and approach, with no obligation.',
    keywords: 'software consultation, project scoping, development quote, free consultation',
    canonicalPath: '/schedule-a-demo',
    ogType: 'website',
  },
  about: {
    title: 'About Us',
    description: 'A software team building custom platforms, integrations and automation for growing businesses.',
    keywords: 'about, software development team, custom development company',
    canonicalPath: '/about',
    ogType: 'website',
  },
  contact: {
    title: 'Contact Us',
    description: 'Get in touch about a software project — custom platforms, integrations and automation.',
    keywords: 'contact, software development, custom application, project inquiry',
    canonicalPath: '/contact',
    ogType: 'website',
  },
  // Still used by ResourcesPage, which remains routed.
  resources: {
    title: 'Resources & Guides',
    description: 'Guides, articles and reference material.',
    keywords: 'resources, guides, articles',
    canonicalPath: '/123-bots-resources',
    ogType: 'website',
  },
  faq: {
    title: 'Frequently Asked Questions',
    description: 'Common questions about how projects are scoped, priced, built and supported.',
    keywords: 'FAQ, software development questions, pricing, project process, support',
    canonicalPath: '/faq',
    ogType: 'website',
  },
};

// Export site constants for use elsewhere
export { SITE_NAME, SITE_URL, DEFAULT_IMAGE, TWITTER_HANDLE };
